import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Download,
  Filter,
  Calendar as CalendarIcon,
  RefreshCw,
  SlidersHorizontal,
  CircleAlert,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  buyerColumns,
  invoiceColumns,
  supplierColumns,
  eInvoiceColumns,
} from "@/constants/reportColumns";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { invoiceTypes } from "@/constants/invoiceTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBranch } from "@/hooks/useBranch";
import { api } from "@/utils/axiosInterceptor";
import type { AxiosResponse } from "axios";
import { useToast } from "@/hooks/use-toast";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import LoadingIndicator from "./LoadingIndicator";
import { toCalendarDay } from "@/utils/dateRange";
import { getInvoiceStatusLabel } from "@/constants/invoiceStatusLabels";

/**
 * Report status filters, in the same order as the Invoices page.
 */
const statuses = [
  "Draft",
  "Submitting",
  "Submitted",
  "Valid",
  "Invalid",
  "PendingCheck",
  "DuplicateConflict",
  "Consolidated",
  "Cancelled",
  "Deleted",
];

const isEditedViaSelfServiceStatus = ["self-service", "non-self-service"];

const ReportPage = () => {
  // const { user } = useAuth();
  const { branches, isLoading } = useBranch("");
  const { toast } = useToast();
  const defaultSelectedInvoiceColumns = invoiceColumns.reduce(
    (acc, col) => {
      acc[col.value] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedBuyerColumns = buyerColumns.reduce(
    (acc, col) => {
      acc[col.value] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedSupplierColumns = supplierColumns.reduce(
    (acc, col) => {
      acc[col.value] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedEInvoiceColumns = eInvoiceColumns.reduce(
    (acc, col) => {
      acc[col.value] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedInvoiceType = invoiceTypes.reduce(
    (acc, col) => {
      acc[col.Code] = col.Code !== "01A";
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedBranches = branches.reduce(
    (acc, branch) => {
      acc[branch.id] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedStatus = statuses.reduce(
    (acc, status) => {
      acc[status] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const defaultSelectedIsEditedViaSelfService =
    isEditedViaSelfServiceStatus.reduce(
      (acc, selfService) => {
        acc[selfService] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  const [selectedInvoiceColumns, setSelectedInvoiceColumns] = useState(
    defaultSelectedInvoiceColumns,
  );
  const [selectedBuyerColumns, setSelectedBuyerColumns] = useState(
    defaultSelectedBuyerColumns,
  );
  const [selectedSupplierColumns, setSelectedSupplierColumns] = useState(
    defaultSelectedSupplierColumns,
  );
  const [selectedEInvoiceColumns, setSelectedEInvoiceColumns] = useState(
    defaultSelectedEInvoiceColumns,
  );
  const [selectedInvoiceType, setSelectedInvoiceType] = useState(
    defaultSelectedInvoiceType,
  );
  const [selectedBranches, setSelectedBranches] = useState(
    defaultSelectedBranches,
  );
  const [selectedStatus, setSelectedStatus] = useState(defaultSelectedStatus);
  const [selectedIsEditedViaSelfService, setSelectedIsEditedViaSelfService] =
    useState(defaultSelectedIsEditedViaSelfService);
  const [invoiceColumnSearch, setInvoiceColumnSearch] = useState("");
  const [supplierColumnSearch, setSupplierColumnSearch] = useState("");
  const [buyerColumnSearch, setBuyerColumnSearch] = useState("");
  const [eInvoiceColumnsSearch, setEInvoiceColumnsSearch] = useState("");
  const [invoiceTypeSearch, setInvoiceTypeSearch] = useState("");
  const [branchSearch, setBranchSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");
  const [isEditedViaSelfServiceSearch, setIsEditedViaSelfServiceSearch] =
    useState("");
  const [issuanceDateRange, setIssuanceDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [invoiceDateRange, setInvoiceDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [filters, setFilters] = useState({
    limit: undefined,
    offset: undefined,
    invoiceNumberFrom: undefined,
    invoiceNumberTo: undefined,
    posReferenceNumberFrom: undefined,
    posReferenceNumberTo: undefined,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportFormat, setReportFormat] = useState("xlsx");

  useEffect(() => {
    if (!isLoading && branches.length > 0) {
      setSelectedBranches(
        branches.reduce(
          (acc, branch) => {
            acc[branch.id] = true; // auto-tick all
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      );
    }
  }, [branches, isLoading]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value == "" ? undefined : value,
    }));
  };

  const pollReportJob = async (jobId: string): Promise<any[]> => {
    const POLL_INTERVAL = 3000;
    const MAX_POLLS = 200;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

      let response: AxiosResponse<any>;
      try {
        response = await api.get(`/invoices/report/job/${jobId}`);
      } catch (err: any) {
        const status = err?.response?.status;
        // Retry on transient gateway/server errors; throw on definitive errors
        if (status && status < 500) throw err;
        // 500/502/503/504 and network blips — log and keep polling
        console.error(
          `Poll attempt ${i + 1} failed with status ${status ?? "network error"}, retrying...`,
        );
        continue;
      }

      if (response.status !== 200) continue;

      if (Array.isArray(response.data)) return response.data;

      throw new Error(
        typeof response.data === "string"
          ? "The report response was incomplete and could not be read. This usually means the connection dropped while the report was downloading — try again, or narrow the filters to produce a smaller report."
          : `Unexpected report response format (received ${typeof response.data}). Please try again.`,
      );
    }

    throw new Error("Report generation timed out after 10 minutes");
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      const requestBody = {
        columns: [
          selectedInvoiceColumns,
          selectedEInvoiceColumns,
          selectedBuyerColumns,
          selectedSupplierColumns,
        ].flatMap((colObj) =>
          Object.entries(colObj)
            .filter(([_, value]) => value)
            .map(([key, _]) => key),
        ),
        filters: {
          invoiceDate: {
            gte: toCalendarDay(issuanceDateRange.from),
            lte: toCalendarDay(issuanceDateRange.to),
          },
          invoiceType: Object.entries(selectedInvoiceType)
            .filter(([_, value]) => value)
            .map(([key, _]) => key),
          voucher: {
            gte: filters.invoiceNumberFrom,
            lte: filters.invoiceNumberTo,
          },
          branch: Object.entries(selectedBranches)
            .filter(([_, value]) => value)
            .map(([key, _]) => key),
          documentDate: {
            gte: toCalendarDay(invoiceDateRange.from),
            lte: toCalendarDay(invoiceDateRange.to),
          },
          originalInvoiceNumber: {
            gte: filters.posReferenceNumberFrom,
            lte: filters.posReferenceNumberTo,
          },
          status: Object.entries(selectedStatus)
            .filter(([_, value]) => value)
            .map(([key, _]) => key),
          isEditedViaSelfService: Object.entries(selectedIsEditedViaSelfService)
            .filter(([_, value]) => value)
            .map(([key, _]) => key == "self-service"),
        },
        limit: filters.limit ? Number(filters.limit) : undefined,
        offset: filters.offset ? Number(filters.offset) : undefined,
      };

      // Start the async job (returns immediately with jobId)
      const startResponse = await api.post(
        "/invoices/report/start",
        requestBody,
      );
      const { jobId } = startResponse.data ?? {};
      if (!jobId) {
        throw new Error(
          "The server did not return a report job id. Please try again.",
        );
      }

      // Poll until the report is ready
      const reportData = await pollReportJob(jobId);

      if (reportData.length <= 0) {
        throw new Error(
          "No invoice found, insufficient data to generate report",
        );
      }

      // Amounts are exported exactly as stored
      const worksheet = XLSX.utils.json_to_sheet(reportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "E-Invoices");
      switch (reportFormat) {
        case "xlsx": {
          XLSX.writeFile(workbook, "E-Invoice.xlsx", { compression: true });
          break;
        }
        case "csv": {
          XLSX.writeFile(workbook, "E-Invoice.csv", { compression: true });
          break;
        }
        case "pdf": {
          const doc = new jsPDF("landscape");
          const columns = Object.keys(reportData[0]).map((key) => ({
            header: key,
            dataKey: key,
          }));

          doc.text("E-Invoices Report", 14, 10);
          autoTable(doc, {
            columns,
            body: reportData,
            startY: 20,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [214, 214, 214] }, // gray header
            theme: "plain",
            horizontalPageBreak: true,
            horizontalPageBreakBehaviour: "immediately",
            didParseCell: (data) => {
              const val = String(data.cell.raw ?? "").trim();
              // Prevent line-wrapping for currency ("RM 1,000.00") or numeric values
              if (
                /^[A-Z]{2,3}\s[\d,]+(\.\d+)?$/.test(val) ||
                /^-?[\d,]+(\.\d+)?$/.test(val)
              ) {
                data.cell.styles.minCellWidth = 30;
              }
            },
          });

          window.open(doc.output("bloburl"));
          break;
        }
      }
    } catch (error: any) {
      console.error("Report generation failed:", error);

      let errorMessage = "Failed to generate report. Please try again.";

      if (Array.isArray(error.response?.data?.details)) {
        errorMessage = error.response.data.details
          .map((d: any) => d.message)
          .join(", ");
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Oops! Unable to generate report.",
        description: errorMessage,
        variant: "destructive",
        icon: (
          <CircleAlert size={30} className="animate-spin-once text-red-400" />
        ),

        duration: 20000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                <FileText className="h-8 w-8 mr-3 text-blue-600" />
                LHDN E-Invoice Report Generator
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Generate comprehensive PDF reports for Malaysian e-invoice
                submissions with advanced filtering options
              </p>
            </div>
            <Badge variant="outline" className="flex items-center">
              <Filter className="h-3 w-3 mr-1" />
              Report Generator
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filter Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Column selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  Report Columns
                </CardTitle>
                <CardDescription>
                  Select the report columns for invoice report
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Invoice columns */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 justify-start truncate"
                    >
                      <SlidersHorizontal />
                      Invoice Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">Invoice Column</p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected = invoiceColumns.every(
                            (col) => selectedInvoiceColumns[col.value],
                          );

                          // if all selected → deselect all, else select all
                          const updated = invoiceColumns.reduce(
                            (acc, col) => {
                              acc[col.value] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedInvoiceColumns(updated);
                        }}
                      >
                        {invoiceColumns.every(
                          (col) => selectedInvoiceColumns[col.value],
                        )
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={invoiceColumnSearch}
                        onChange={(e) => setInvoiceColumnSearch(e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {invoiceColumns
                        .filter((col) =>
                          col.label
                            .toLocaleLowerCase()
                            .includes(invoiceColumnSearch.toLocaleLowerCase()),
                        )
                        .map((invCol) => (
                          <DropdownMenuCheckboxItem
                            key={invCol.value}
                            checked={!!selectedInvoiceColumns[invCol.value]} // ✅ lookup in object
                            onCheckedChange={(checked) =>
                              setSelectedInvoiceColumns((prev) => ({
                                ...prev,
                                [invCol.value]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {invCol.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* E Invoice columns */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 justify-start truncate"
                    >
                      <SlidersHorizontal />E Invoice Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">E Invoice Column</p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected = eInvoiceColumns.every(
                            (col) => selectedEInvoiceColumns[col.value],
                          );

                          // if all selected → deselect all, else select all
                          const updated = eInvoiceColumns.reduce(
                            (acc, col) => {
                              acc[col.value] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedEInvoiceColumns(updated);
                        }}
                      >
                        {eInvoiceColumns.every(
                          (col) => selectedEInvoiceColumns[col.value],
                        )
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={eInvoiceColumnsSearch}
                        onChange={(e) =>
                          setEInvoiceColumnsSearch(e.target.value)
                        }
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {eInvoiceColumns
                        .filter((col) =>
                          col.label
                            .toLocaleLowerCase()
                            .includes(
                              eInvoiceColumnsSearch.toLocaleLowerCase(),
                            ),
                        )
                        .map((invCol) => (
                          <DropdownMenuCheckboxItem
                            key={invCol.value}
                            checked={!!selectedEInvoiceColumns[invCol.value]} // ✅ lookup in object
                            onCheckedChange={(checked) =>
                              setSelectedEInvoiceColumns((prev) => ({
                                ...prev,
                                [invCol.value]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {invCol.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Buyer columns */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 justify-start truncate"
                    >
                      <SlidersHorizontal />
                      Buyer Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">Buyer Column</p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected = buyerColumns.every(
                            (col) => selectedBuyerColumns[col.value],
                          );

                          // if all selected → deselect all, else select all
                          const updated = buyerColumns.reduce(
                            (acc, col) => {
                              acc[col.value] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedBuyerColumns(updated);
                        }}
                      >
                        {buyerColumns.every(
                          (col) => selectedBuyerColumns[col.value],
                        )
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={buyerColumnSearch}
                        onChange={(e) => setBuyerColumnSearch(e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {buyerColumns
                        .filter((col) =>
                          col.label
                            .toLocaleLowerCase()
                            .includes(buyerColumnSearch.toLocaleLowerCase()),
                        )
                        .map((invCol) => (
                          <DropdownMenuCheckboxItem
                            key={invCol.value}
                            checked={!!selectedBuyerColumns[invCol.value]} // ✅ lookup in object
                            onCheckedChange={(checked) =>
                              setSelectedBuyerColumns((prev) => ({
                                ...prev,
                                [invCol.value]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {invCol.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Supplier columns */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 justify-start truncate"
                    >
                      <SlidersHorizontal />
                      Supplier Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">Supplier Column</p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected = supplierColumns.every(
                            (col) => selectedSupplierColumns[col.value],
                          );

                          // if all selected → deselect all, else select all
                          const updated = supplierColumns.reduce(
                            (acc, col) => {
                              acc[col.value] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedSupplierColumns(updated);
                        }}
                      >
                        {supplierColumns.every(
                          (col) => selectedSupplierColumns[col.value],
                        )
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={supplierColumnSearch}
                        onChange={(e) =>
                          setSupplierColumnSearch(e.target.value)
                        }
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {supplierColumns
                        .filter((col) =>
                          col.label
                            .toLocaleLowerCase()
                            .includes(supplierColumnSearch.toLocaleLowerCase()),
                        )
                        .map((invCol) => (
                          <DropdownMenuCheckboxItem
                            key={invCol.value}
                            checked={!!selectedSupplierColumns[invCol.value]} // ✅ lookup in object
                            onCheckedChange={(checked) =>
                              setSelectedSupplierColumns((prev) => ({
                                ...prev,
                                [invCol.value]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {invCol.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>

            {/* Date Range Filter */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {/* <CalendarIcon className="h-5 w-5 mr-2" /> */}
                    Issuance Date Range
                  </CardTitle>
                  <CardDescription>
                    Filters on the <strong>Issuance Date</strong> column — the
                    same date as the "Issuance" filter on the Invoices page
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !issuanceDateRange.from &&
                            !issuanceDateRange.to &&
                            "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {issuanceDateRange.from ? (
                          issuanceDateRange.to ? (
                            <>
                              {format(issuanceDateRange.from, "PP")} -{" "}
                              {format(issuanceDateRange.to, "PP")}
                            </>
                          ) : (
                            format(issuanceDateRange.from, "PP")
                          )
                        ) : (
                          "Pick a date range"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={issuanceDateRange}
                        onSelect={(range) =>
                          setIssuanceDateRange({
                            from: range?.from,
                            to: range?.to,
                          })
                        }
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    Invoice Type
                  </CardTitle>
                  <CardDescription>
                    Select the invoice type for invoice report
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  {/* Invoice type */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex-1 w-full justify-start truncate mt-auto"
                      >
                        <SlidersHorizontal />
                        Invoice Type
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="" align="start">
                      <div className="flex justify-between m-2">
                        <p className="text-sm font-medium">Invoice Type</p>
                        <a
                          className="text-sm text-center cursor-pointer hover:underline"
                          onClick={() => {
                            // check if all are already selected
                            const allSelected = invoiceTypes.every(
                              (col) => selectedInvoiceType[col.Code],
                            );

                            // if all selected → deselect all, else select all
                            const updated = invoiceTypes.reduce(
                              (acc, col) => {
                                acc[col.Code] = !allSelected;
                                return acc;
                              },
                              {} as Record<string, boolean>,
                            );

                            setSelectedInvoiceType(updated);
                          }}
                        >
                          {invoiceTypes.every(
                            (col) => selectedInvoiceType[col.Code],
                          )
                            ? "Deselect All"
                            : "Select All"}
                        </a>
                      </div>
                      <div className="flex">
                        <Input
                          className="h-8 m-1"
                          placeholder="Search..."
                          value={invoiceTypeSearch}
                          onChange={(e) => setInvoiceTypeSearch(e.target.value)}
                          onKeyDown={(e) => {
                            // Prevent Radix from using this key for typeahead navigation
                            e.stopPropagation();
                          }}
                        />
                      </div>
                      <div className="max-h-64 min-w-60 overflow-y-auto">
                        {invoiceTypes
                          .filter((invType) =>
                            invType.Description.toLocaleLowerCase().includes(
                              invoiceTypeSearch.toLocaleLowerCase(),
                            ),
                          )
                          .map((invType) => (
                            <DropdownMenuCheckboxItem
                              key={invType.Code}
                              checked={!!selectedInvoiceType[invType.Code]} // ✅ lookup in object
                              onCheckedChange={(checked) =>
                                setSelectedInvoiceType((prev) => ({
                                  ...prev,
                                  [invType.Code]: checked,
                                }))
                              }
                              onSelect={(event) => event.preventDefault()}
                            >
                              {invType.Description}
                            </DropdownMenuCheckboxItem>
                          ))}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardContent>
              </Card>
            </div>

            {/* Invoice Number Range */}
            <Card>
              <CardHeader>
                <CardTitle>Invoice Number Range</CardTitle>
                <CardDescription>
                  Filter by invoice number range
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoiceNumberFrom">From Invoice Number</Label>
                  <Input
                    id="invoiceNumberFrom"
                    placeholder="e.g., INV-2024-0001"
                    value={filters.invoiceNumberFrom}
                    onChange={(e) =>
                      handleFilterChange("invoiceNumberFrom", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="invoiceNumberTo">To Invoice Number</Label>
                  <Input
                    id="invoiceNumberTo"
                    placeholder="e.g., INV-2024-9999"
                    value={filters.invoiceNumberTo}
                    onChange={(e) =>
                      handleFilterChange("invoiceNumberTo", e.target.value)
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Branches */}
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center">Branches</CardTitle>
                <CardDescription>
                  Select the branches for invoice report
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                {/* Branches */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 w-full justify-start truncate mt-auto"
                    >
                      <SlidersHorizontal />
                      Branches
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">Branches</p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected = branches.every(
                            (branch) => selectedBranches[branch.id],
                          );

                          // if all selected → deselect all, else select all
                          const updated = branches.reduce(
                            (acc, col) => {
                              acc[col.id] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedBranches(updated);
                        }}
                      >
                        {branches.every((col) => selectedBranches[col.id])
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={branchSearch}
                        onChange={(e) => setBranchSearch(e.target.value)}
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {branches
                        .filter(
                          (branch) =>
                            branch.name
                              .toLocaleLowerCase()
                              .includes(branchSearch.toLocaleLowerCase()) ||
                            branch.prefixCode
                              .toLocaleLowerCase()
                              .includes(branchSearch.toLocaleLowerCase()),
                        )
                        .map((branch) => (
                          <DropdownMenuCheckboxItem
                            key={branch.id}
                            checked={!!selectedBranches[branch.id]}
                            onCheckedChange={(checked) =>
                              setSelectedBranches((prev) => ({
                                ...prev,
                                [branch.id]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {branch.prefixCode} - {branch.name}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {/* <CalendarIcon className="h-5 w-5 mr-2" /> */}
                    Invoice Date Range
                  </CardTitle>
                  <CardDescription>
                    Filters on the <strong>Invoice Date</strong> column (the
                    original document date)
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !invoiceDateRange.from &&
                            !invoiceDateRange.to &&
                            "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {invoiceDateRange.from ? (
                          invoiceDateRange.to ? (
                            <>
                              {format(invoiceDateRange.from, "PP")} -{" "}
                              {format(invoiceDateRange.to, "PP")}
                            </>
                          ) : (
                            format(invoiceDateRange.from, "PP")
                          )
                        ) : (
                          "Pick a date range"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={invoiceDateRange}
                        onSelect={(range) =>
                          setInvoiceDateRange({
                            from: range?.from,
                            to: range?.to,
                          })
                        }
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    E-Invoice Status
                  </CardTitle>
                  <CardDescription>Select the E-Invoice Status</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  {/* E-Invoice Status */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex-1 w-full justify-start truncate mt-auto"
                      >
                        <SlidersHorizontal />
                        E-Invoice Status
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="" align="start">
                      <div className="flex justify-between m-2">
                        <p className="text-sm font-medium">E-Invoice Status</p>
                        <a
                          className="text-sm text-center cursor-pointer hover:underline"
                          onClick={() => {
                            // check if all are already selected
                            const allSelected = statuses.every(
                              (status) => selectedStatus[status],
                            );

                            // if all selected → deselect all, else select all
                            const updated = statuses.reduce(
                              (acc, status) => {
                                acc[status] = !allSelected;
                                return acc;
                              },
                              {} as Record<string, boolean>,
                            );

                            setSelectedStatus(updated);
                          }}
                        >
                          {statuses.every((status) => selectedStatus[status])
                            ? "Deselect All"
                            : "Select All"}
                        </a>
                      </div>
                      <div className="flex">
                        <Input
                          className="h-8 m-1"
                          placeholder="Search..."
                          value={statusSearch}
                          onChange={(e) => setStatusSearch(e.target.value)}
                          onKeyDown={(e) => {
                            // Prevent Radix from using this key for typeahead navigation
                            e.stopPropagation();
                          }}
                        />
                      </div>
                      <div className="max-h-64 min-w-60 overflow-y-auto">
                        {statuses
                          .filter((status) =>
                            // Search the visible label
                            getInvoiceStatusLabel(status)
                              .toLocaleLowerCase()
                              .includes(statusSearch.toLocaleLowerCase()),
                          )
                          .map((status) => (
                            <DropdownMenuCheckboxItem
                              key={status}
                              checked={!!selectedStatus[status]} // ✅ lookup in object
                              onCheckedChange={(checked) =>
                                setSelectedStatus((prev) => ({
                                  ...prev,
                                  [status]: checked,
                                }))
                              }
                              onSelect={(event) => event.preventDefault()}
                            >
                              {getInvoiceStatusLabel(status)}
                            </DropdownMenuCheckboxItem>
                          ))}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardContent>
              </Card>
            </div>

            {/* POS reference number range */}
            <Card>
              <CardHeader>
                <CardTitle>POS Reference Number Range</CardTitle>
                <CardDescription>
                  Filter by POS reference number range
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="posReferenceNumberFrom">
                    From POS Reference Number
                  </Label>
                  <Input
                    id="posReferenceNumberFrom"
                    placeholder="e.g., INV-2024-0001"
                    value={filters.posReferenceNumberFrom}
                    onChange={(e) =>
                      handleFilterChange(
                        "posReferenceNumberFrom",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="posReferenceNumberTo">
                    To POS Reference Number
                  </Label>
                  <Input
                    id="posReferenceNumberTo"
                    placeholder="e.g., INV-2024-9999"
                    value={filters.posReferenceNumberTo}
                    onChange={(e) =>
                      handleFilterChange("posReferenceNumberTo", e.target.value)
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Is Edited Via Self Service */}
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center">
                  Is Edited Via Self Service
                </CardTitle>
                <CardDescription>
                  Select is the invoice edited via self service
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                {/* E-Invoice Status */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 w-full justify-start truncate mt-auto"
                    >
                      <SlidersHorizontal />
                      Is Edited Via Self Service
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[350px]" align="start">
                    <div className="flex justify-between m-2">
                      <p className="text-sm font-medium">
                        Is Edited Via Self Service
                      </p>
                      <a
                        className="text-sm text-center cursor-pointer hover:underline"
                        onClick={() => {
                          // check if all are already selected
                          const allSelected =
                            isEditedViaSelfServiceStatus.every(
                              (selfService) =>
                                selectedIsEditedViaSelfService[selfService],
                            );

                          // if all selected → deselect all, else select all
                          const updated = isEditedViaSelfServiceStatus.reduce(
                            (acc, selfService) => {
                              acc[selfService] = !allSelected;
                              return acc;
                            },
                            {} as Record<string, boolean>,
                          );

                          setSelectedIsEditedViaSelfService(updated);
                        }}
                      >
                        {isEditedViaSelfServiceStatus.every(
                          (selfService) =>
                            selectedIsEditedViaSelfService[selfService],
                        )
                          ? "Deselect All"
                          : "Select All"}
                      </a>
                    </div>
                    <div className="flex">
                      <Input
                        className="h-8 m-1"
                        placeholder="Search..."
                        value={isEditedViaSelfServiceSearch}
                        onChange={(e) =>
                          setIsEditedViaSelfServiceSearch(e.target.value)
                        }
                        onKeyDown={(e) => {
                          // Prevent Radix from using this key for typeahead navigation
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    <div className="max-h-64 min-w-60 overflow-y-auto">
                      {isEditedViaSelfServiceStatus
                        .filter((selfService) =>
                          selfService
                            .toLocaleLowerCase()
                            .includes(
                              isEditedViaSelfServiceSearch.toLocaleLowerCase(),
                            ),
                        )
                        .map((selfService) => (
                          <DropdownMenuCheckboxItem
                            key={selfService}
                            checked={
                              !!selectedIsEditedViaSelfService[selfService]
                            } // ✅ lookup in object
                            onCheckedChange={(checked) =>
                              setSelectedIsEditedViaSelfService((prev) => ({
                                ...prev,
                                [selfService]: checked,
                              }))
                            }
                            onSelect={(event) => event.preventDefault()}
                          >
                            {selfService}
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>

            {/* Customer & Supplier Filter */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Customer & Supplier
                </CardTitle>
                <CardDescription>Filter by customer and supplier information</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer">Customer</Label>
                  <Input
                    id="customer"
                    placeholder="Enter customer name or select from list"
                    value={filters.customer}
                    onChange={(e) => handleFilterChange('customer', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="supplierName">Supplier Name</Label>
                  <Input
                    id="supplierName"
                    placeholder="Enter supplier name or select from list"
                    value={filters.supplierName}
                    onChange={(e) => handleFilterChange('supplierName', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card> */}

            {/* Document Details Filter */}
            {/* <Card>
              <CardHeader>
                <CardTitle>Document Details</CardTitle>
                <CardDescription>Filter by document status, tax type, and invoice type</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Input
                    id="status"
                    placeholder="e.g., Draft, Submitted, Approved"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="taxType">Tax Type</Label>
                  <Input
                    id="taxType"
                    placeholder="e.g., SST, GST, Zero Rated"
                    value={filters.taxType}
                    onChange={(e) => handleFilterChange('taxType', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="eInvoiceType">E-Invoice Type</Label>
                  <Input
                    id="eInvoiceType"
                    placeholder="e.g., Standard Invoice, Credit Note"
                    value={filters.eInvoiceType}
                    onChange={(e) => handleFilterChange('eInvoiceType', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card> */}
          </div>

          {/* Report Options & Actions */}
          <div className="space-y-6">
            {/* Report Options */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Report Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="reportFormat">Report Format</Label>
                  <Select
                    // onValueChange={field.onChange}
                    value="xlsx"
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="-- Select type --" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="xlsx" value="xlsx">XLSX</SelectItem>
                      <SelectItem key="csv" value="csv">CSV</SelectItem>
                      <SelectItem key="pdf" value="pdf">PDF</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="groupBy">Group By</Label>
                  <Input
                    id="groupBy"
                    value={filters.groupBy}
                    onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                    placeholder="Date, Customer, Branch, Status"
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeDetails"
                      checked={filters.includeDetails}
                      onCheckedChange={(checked) => handleFilterChange('includeDetails', checked)}
                    />
                    <Label htmlFor="includeDetails">Include detailed transactions</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeSummary"
                      checked={filters.includeSummary}
                      onCheckedChange={(checked) => handleFilterChange('includeSummary', checked)}
                    />
                    <Label htmlFor="includeSummary">Include summary statistics</Label>
                  </div>
                </div>
              </CardContent>
            </Card> */}

            {/* Action Buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Generate Report</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="reportFormat">Report Format</Label>
                  <Select
                    onValueChange={(val) => setReportFormat(val)}
                    value={reportFormat}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="-- Select type --" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="xlsx" value="xlsx">
                        XLSX
                      </SelectItem>
                      <SelectItem key="csv" value="csv">
                        CSV
                      </SelectItem>
                      <SelectItem key="pdf" value="pdf">
                        PDF
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-row gap-2 justify-between">
                  <div className="flex-1">
                    <Label>Limit rows</Label>
                    <Input
                      value={filters.limit}
                      onChange={(e) =>
                        handleFilterChange("limit", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Offset</Label>
                    <Input
                      value={filters.offset}
                      onChange={(e) =>
                        handleFilterChange("offset", e.target.value)
                      }
                    />
                  </div>
                </div>

                <Separator />

                <Button
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Generate PDF Report
                    </>
                  )}
                </Button>

                {/* <Button
                  onClick={() => alert('Report preview would open in a new window')} 
                  variant="outline"
                  className="w-full"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Report
                </Button>*/}

                {/* <Button
                  onClick={handleClearFilters} 
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear All Filters
                </Button>*/}
              </CardContent>
            </Card>

            {/* Report Info */}
            <Card>
              <CardHeader>
                <CardTitle>Report Information</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• Real-time generation of reports</p>
                <p>• Customizable column selection</p>
                <p>• Advance filter function</p>
                <p>• Summary and detailed reporting options</p>
                <p>• Supports multiple export formats (PDF, Excel, CSV)</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
